import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useWhiteLabelStore } from "state/store/white-label/useWhiteLabelStore";
import audioBufferToWav from "audiobuffer-to-wav";

const useAudioRecorder = () => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const [isRecording, setIsRecording] = useState(false);
    const audioChunkRef = useRef<Blob[]>([]);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const { settings: whiteLabelSettings } = useWhiteLabelStore();

    // 👇 waveform refs
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const animationRef = useRef<number | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const dataArrayRef = useRef<Uint8Array | null>(null);

    const drawWaveform = useCallback(() => {
        const canvas = canvasRef.current;
        const analyser = analyserRef.current;
        const dataArray = dataArrayRef.current;
        if (!canvas || !analyser || !dataArray) return;

        const ctx = canvas.getContext("2d");
        const bufferLength = analyser.frequencyBinCount;
        const width = canvas.width;
        const height = canvas.height;

        const draw = () => {
            animationRef.current = requestAnimationFrame(draw);
            analyser.getByteTimeDomainData(dataArray);

            ctx!.clearRect(0, 0, width, height);

            ctx!.lineWidth = 1;
            ctx!.strokeStyle = whiteLabelSettings?.themeCustomColor || '#ac9477';
            ctx!.direction = "rtl";
            ctx!.lineCap = "square";
            ctx!.lineJoin = "miter";
            ctx!.beginPath();

            const sliceWidth = width / bufferLength;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                const v = dataArray[i] / 128.0;
                const y = (v * height) / 2;
                i === 0 ? ctx!.moveTo(x, y) : ctx!.lineTo(x, y);
                x += sliceWidth;
            }

            ctx!.lineTo(width, height / 2);
            ctx!.stroke();
        };

        draw();
    }, [whiteLabelSettings?.themeCustomColor]);

    const convertToWav = async (audioBlob: Blob): Promise<Blob> => {
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const wavArrayBuffer = audioBufferToWav(audioBuffer);

        return new Blob([wavArrayBuffer], { type: 'audio/wav' });
    };

    const startRecording = useCallback(async () => {
        try {
            if (typeof window === "undefined") {
                throw new Error("Microphone access only works in the browser");
            }

            if (!navigator?.mediaDevices?.getUserMedia) {
                throw new Error("Browser does not support microphone access");
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 2048;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            dataArrayRef.current = dataArray;
            source.connect(analyser);

            audioContextRef.current = audioContext;
            analyserRef.current = analyser;

            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunkRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunkRef.current.push(event.data);
                }
            }

            mediaRecorderRef.current.onstart = () => {
                setIsRecording(true);
            }

            mediaRecorderRef.current.start();

            // drawWaveform();
        } catch (error) {
            toast({
                variant: 'destructive',
                title: t('FAILED'),
                description: t('MICROPHONE_ACCESS_DENIED_OR_FAILED'),
            })
            console.error('Microphone access denied or failed:', error);
            setIsRecording(false);
            throw error;
        }
    }, [drawWaveform])

    const stopRecording = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (audioContextRef.current) audioContextRef.current.close();

            if (!mediaRecorderRef.current) {
                reject(new Error('MediaRecorder is not initialized'));
                return;
            }

            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.onstop = async () => {
                    setIsRecording(false);
                    const audioBlob = new Blob(audioChunkRef.current, { type: mediaRecorderRef?.current?.mimeType });
                    try {
                        const wavBlob = await convertToWav(audioBlob);
                        resolve(wavBlob);
                    } catch (error) {
                        console.error('Failed to convert audio to WAV:', error);
                        reject(error);
                    }
                }
                mediaRecorderRef.current.stop();
                mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            } else {
                resolve(null);
            }
        })
    }, [])

    const cancelRecording = () => {
        if (!isRecording) return;
        setIsRecording(false);
        if (!mediaRecorderRef.current) return;
        mediaRecorderRef.current.stop();
    }

    useEffect(() => {
        if (isRecording) {
            drawWaveform();
        } else {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        }
    }, [isRecording, drawWaveform]);


    return {
        isRecording,
        startRecording,
        stopRecording,
        cancelRecording,
        canvasRef,
    }


}

export default useAudioRecorder;
